/*!
 * Copyright © 2025. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { PendoTrackEventService } from '../../../external/services';
import { EPendoCustomEvents } from '../../models';
import { IDeviceHealthFilterRequest } from '../../models/device-health.interface';
import { PlotHttpService } from '../../services';
import {
  EDeviceHealthAction,
  GetAllDeviceHealthStatusError,
  GetAllDeviceHealthStatusSuccess,
  GetCountOfAllDeviceIssues,
  GetCountOfAllDeviceIssuesError,
  GetSelectedDeviceHealthStatus,
  GetSelectedDeviceHealthStatusSuccess,
  MapBalloonOpenWithDeviceHealthIssue,
  SelectVehicleIdForDeviceHealth,
  SelectVehicleIdForDeviceHealthSuccess
} from '../actions/device-health.actions';
import {
  getAllDevicesHealth,
  getBalloon,
  getIsSelfServiceExperimentIssueBadgeEnabled,
  getSelectedAssetIdsForDeviceHealth,
  getSelectedVehicleIdsForDeviceHealth
} from '../selectors';
import { IBalloonInfoState, ICustomerMetadataState, IDeviceHealthState, IUserMapSettingsState } from '../state';

@Injectable()
export class DeviceHealthEffects {
  constructor(
    private readonly _actions$: Actions,
    private readonly _customerStore: Store<ICustomerMetadataState>,
    private readonly _devicehealthStore: Store<IDeviceHealthState>,
    private readonly _balloonStore: Store<IBalloonInfoState>,
    private readonly _userState: Store<IUserMapSettingsState>,
    private readonly _plotHttpService: PlotHttpService,
    private readonly _pendoTrackEventService: PendoTrackEventService
  ) {}
  public getAllDevicesHealth$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetSelectedDeviceHealthStatus>(EDeviceHealthAction.GetSelectedDeviceHealthStatus),
      withLatestFrom(
        this._customerStore.pipe(select(getSelectedVehicleIdsForDeviceHealth)),
        this._customerStore.pipe(select(getSelectedAssetIdsForDeviceHealth)),
        this._userState.pipe(select(getIsSelfServiceExperimentIssueBadgeEnabled))
      ),
      switchMap(([_, selectedVehicleIds, selectedAssetIds, featureEnabled]) => {
        const request: IDeviceHealthFilterRequest = {
          AssetIds: Array.from(selectedAssetIds).map(x =>
            x.startsWith('a') || x.startsWith('v') ? parseInt(x.substring(1)) : parseInt(x)
          ),
          VehicleIds: Array.from(selectedVehicleIds).map(x => (x.startsWith('v') ? parseInt(x.substring(1)) : parseInt(x)))
        };
        return this._plotHttpService.getAllDevicesHealth(request, featureEnabled).pipe(
          map(response => new GetSelectedDeviceHealthStatusSuccess(response)),
          catchError(err => {
            return of(new GetAllDeviceHealthStatusError(err));
          })
        );
      })
    )
  );

  public selectVehicleIdForDeviceHealth$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<SelectVehicleIdForDeviceHealth>(EDeviceHealthAction.SelectVehicleIdForDeviceHealth),
      map(action => action.vehicleId),
      switchMap(vehicleId =>
        this._balloonStore.pipe(
          select(getBalloon),
          filter(balloon => balloon && String(balloon.id) === vehicleId),
          take(1)
        )
      ),
      withLatestFrom(this._devicehealthStore.pipe(select(getAllDevicesHealth))),
      switchMap(([balloon, devicesHealth]) => {
        const vehicleId = String(balloon.id);
        if (devicesHealth.findIndex(x => x.vehicleId == vehicleId) == -1) {
          return [new GetSelectedDeviceHealthStatus(), new SelectVehicleIdForDeviceHealthSuccess(vehicleId)];
        } else {
          return of(new SelectVehicleIdForDeviceHealthSuccess(vehicleId));
        }
      })
    )
  );

  public sendPendoTrackEvent$: Observable<void> = createEffect(
    () =>
      this._actions$.pipe(
        ofType<MapBalloonOpenWithDeviceHealthIssue>(EDeviceHealthAction.MapBalloonOpenWithDeviceHealthIssue),
        map(action => action.vehicleId),
        withLatestFrom(this._devicehealthStore.pipe(select(getAllDevicesHealth))),
        map(([vehicleId, devicesHealth]) => {
          const deviceHealth = devicesHealth.find(x => x.vehicleId == vehicleId);
          if (deviceHealth) {
            const properties = {
              accountId: deviceHealth.accountId,
              vehicleId: deviceHealth.vehicleId,
              issueCategory: deviceHealth.healthStatus,
              countTrackerIssues: deviceHealth.componentHealth.length,
              trackerIssues: deviceHealth.componentHealth.map(x => x.Observation).join(),
              countCameraIssues: deviceHealth.cameraHealth.length,
              cameraIssues: deviceHealth.cameraHealth.map(x => x.Observation).join()
            };
            this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.LiveMapBalloonWithDeviceIssue, properties);
          }
        })
      ),
    { dispatch: false }
  );

  public getCountOfAllDeviceIssues$: Observable<Action> = createEffect(() =>
    this._actions$.pipe(
      ofType<GetCountOfAllDeviceIssues>(EDeviceHealthAction.GetCountOfAllDeviceIssues),
      withLatestFrom(this._userState.pipe(select(getIsSelfServiceExperimentIssueBadgeEnabled))),
      switchMap(([_, featureExp1Enabled]) => {
        return this._plotHttpService.getCountOfAllDeviceIssuesInFleet(featureExp1Enabled).pipe(
          map(response => new GetAllDeviceHealthStatusSuccess(response.devices, response.countOfDevicesWithError)),
          catchError(err => {
            return of(new GetCountOfAllDeviceIssuesError(err));
          })
        );
      })
    )
  );
}
